import { Group } from '@seen/core/groups'
import type { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { Role } from '@seen/core/roles'
import { kebabCase, uniq } from 'lodash-es'

export interface KnockPractitionerUserProfile {
  name: string
  email?: string | undefined
  practitioner_id: string
  practitioner_reference: string
  practitioner_groups: Group[]
  practitioner_roles: Role[]
  practitioner_cohorts: string[]
  practitioner_group_slugs: string[]
  practitioner_role_slugs: string[]
  orbit_tags: string[]
  is_care_team: boolean
  is_idt: boolean
  is_tech: boolean
  is_moe: boolean
}

interface EmailTrait {
  email?: string
}

export function buildKnockPractitionerUserProfile(
  practitioner: ExtendedPractitioner
): KnockPractitionerUserProfile {
  const groups: Group[] = sortedUnique(practitioner.groups).filter(
    (group) => group !== Group.AUDITOR
  )
  const roles = sortedUnique(practitioner.roles)
  const hasGroup = (group: Group): boolean => groups.includes(group)
  const hasRole = (role: Role): boolean => roles.includes(role)

  const isIdt = hasGroup(Group.IDT) || practitioner.isIDT()
  const isTech = hasGroup(Group.TECH) || hasRole(Role.TECH)

  const cohortFlags = [
    ['care-team', hasGroup(Group.CARE_TEAM)],
    ['idt', isIdt],
    ['tech', isTech],
    ['moe', hasGroup(Group.MOE)],
  ] as const
  const practitionerCohorts = cohortFlags.flatMap(([cohort, enabled]) =>
    enabled ? [cohort] : []
  )

  const emailTrait: EmailTrait = {}
  if (practitioner.primaryEmail) {
    emailTrait.email = practitioner.primaryEmail
  }

  return {
    name: practitioner.name,
    ...emailTrait,
    practitioner_id: practitioner.id,
    practitioner_reference: practitioner.reference.reference,
    practitioner_groups: groups,
    practitioner_roles: roles,
    practitioner_cohorts: practitionerCohorts,
    practitioner_group_slugs: groups.map((group) => kebabCase(group)),
    practitioner_role_slugs: roles.map((role) => kebabCase(role)),
    orbit_tags: [
      ...groups.map((group) => `group:${group}`),
      ...roles.map((role) => `role:${role}`),
      ...practitionerCohorts.map((cohort) => `cohort:${cohort}`),
    ],
    is_care_team: hasGroup(Group.CARE_TEAM),
    is_idt: isIdt,
    is_tech: isTech,
    is_moe: hasGroup(Group.MOE),
  }
}

function sortedUnique<T extends string>(values: T[] | undefined): T[] {
  return uniq(values ?? []).sort()
}
